//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';

import LoadingShimmer from '@components/atoms/loading-shimmer/LoadingShimmer';
import StatText from '@components/atoms/stat-bubble/StatText';
import {
  ChipContainer,
  LoadingShimmerWrapper,
  StatContainer,
  StyledBodyLabelText,
  StyledChip,
  StyledStatBubbleSheet,
} from '@components/molecules/stat-bubble/StatBubbleStyles';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';

export interface IStatBubbleComparisonStat {
  trendingIcon: UnisonIconType;
  percentage: string;
  tooltipText?: string | undefined;
}

interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  helpText?: string;
  alertText?: string | React.ReactElement;
  value: string | number;
  comparisonStat?: IStatBubbleComparisonStat;
  isExportReport?: boolean;
  isLoading?: boolean;
}

export type Props = IOwnProps;

/**
 * The StatBubble molecule is a sheet with our standard stat bubble
 * styling, with the optional ability to add a child atom to display
 * below the stat.
 */
export const StatBubble = ({
  alertText,
  children,
  className,
  comparisonStat,
  helpText,
  isExportReport = false,
  isLoading = false,
  title,
  value,
}: Props) => {
  const getChipType = (chipType: string) => {
    switch (chipType) {
      case 'trending-up':
        return 'success';
      case 'trending-down':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderComparison = () =>
    comparisonStat && (
      <SmallTooltip
        content={comparisonStat.tooltipText}
        isDisabled={!isDefined(comparisonStat.tooltipText)}
        placement='right'
        target={
          <ChipContainer>
            <StyledChip
              chipType={getChipType(comparisonStat.trendingIcon)}
              decorativeIcon={comparisonStat.trendingIcon}
              size='large'
              text={comparisonStat.percentage}
            />
          </ChipContainer>
        }
      />
    );

  return (
    <StyledStatBubbleSheet
      $isExportReport={isExportReport}
      className={className}
    >
      <StyledBodyLabelText
        helpText={isExportReport ? undefined : helpText}
        placement='right'
        title={title}
      />
      <StatContainer>
        {isLoading ? (
          <LoadingShimmerWrapper>
            <LoadingShimmer height='22px' width='100%' />
          </LoadingShimmerWrapper>
        ) : (
          <>
            <StatText
              alertText={alertText}
              mixin={Mixins.Text.Subtitle}
              value={value}
            />
            {renderComparison()}
          </>
        )}
      </StatContainer>
      {children}
    </StyledStatBubbleSheet>
  );
};

export default StatBubble;
